/*
 * Solo Supply - Premium Streetwear Store
 * Main JavaScript File
 * File: assets/solo-supply.js
 */

class SoloSupply {
  constructor() {
    this.init();
  }

  init() {
    this.initHeader();
    this.initMobileMenu();
    this.initCartDrawer();
    this.initPredictiveSearch();
    this.initScrollAnimations();
    this.initToastNotifications();
  }

  // ========================================
  // HEADER SCROLL EFFECT
  // ========================================
  initHeader() {
    const header = document.querySelector('.header-solo');
    if (!header) return;

    let lastScroll = 0;

    window.addEventListener('scroll', () => {
      const currentScroll = window.pageYOffset;

      if (currentScroll > 100) {
        header.classList.add('scrolled');
      } else {
        header.classList.remove('scrolled');
      }

      lastScroll = currentScroll;
    });
  }

  // ========================================
  // MOBILE MENU
  // ========================================
  initMobileMenu() {
    const menuToggle = document.querySelector('[data-mobile-toggle]');
    const mobileMenu = document.querySelector('[data-mobile-menu]');
    const menuClose = document.querySelector('[data-mobile-close]');
    const menuOverlay = document.querySelector('[data-mobile-overlay]');

    if (!menuToggle || !mobileMenu) return;

    const openMenu = () => {
      mobileMenu.classList.add('open');
      menuOverlay?.classList.add('visible');
      menuToggle.classList.add('active');
      document.body.style.overflow = 'hidden';
    };

    const closeMenu = () => {
      mobileMenu.classList.remove('open');
      menuOverlay?.classList.remove('visible');
      menuToggle.classList.remove('active');
      document.body.style.overflow = '';
    };

    menuToggle.addEventListener('click', () => {
      mobileMenu.classList.contains('open') ? closeMenu() : openMenu();
    });

    menuClose?.addEventListener('click', closeMenu);
    menuOverlay?.addEventListener('click', closeMenu);
  }

  // ========================================
  // CART DRAWER (AJAX CART)
  // ========================================
  initCartDrawer() {
    const cartDrawer = document.getElementById('cart-drawer');

    if (!cartDrawer) return;

    // Listen for custom cart update events
    document.addEventListener('cart:updated', () => {
      this.updateCartDrawer();
    });
  }

  // Update cart drawer with current cart data
  async updateCartDrawer() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartItemsContainer = document.querySelector('.cart-drawer__items');
      const cartSubtotal = document.querySelector('[data-cart-subtotal]');
      const cartCount = document.querySelector('[data-cart-count]');

      if (!cartItemsContainer) return;

      // Update ALL cart counts (Header + Drawer)
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
      });

      // Update subtotal
      if (cartSubtotal) {
        cartSubtotal.textContent = this.formatMoney(cart.total_price);
      }

      // Update cart drawer state
      const drawer = document.getElementById('cart-drawer');
      if (drawer) {
        if (cart.item_count === 0) {
          drawer.classList.add('is-empty');
        } else {
          drawer.classList.remove('is-empty');
        }
      }

      // Render cart items
      if (cart.items.length === 0) {
        cartItemsContainer.innerHTML = `
          <div class="cart-drawer__empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" opacity="0.3">
              <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>
            </svg>
            <p>Your cart is empty</p>
            <a href="${window.routes?.all_products_url || '/collections'}" class="btn btn--primary" style="margin-top:1rem;">Shop Now</a>
          </div>
        `;
      } else {
        cartItemsContainer.innerHTML = cart.items.map(item => this.renderCartItem(item)).join('');
        this.initCartItemListeners();
      }
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  }

  // Render a single cart item (Matches cart-drawer.liquid premium structure)
  renderCartItem(item) {
    const formattedPrice = this.formatMoney(item.final_line_price);
    return `
      <div class="cart-item" data-line-item="${item.key}">
        <a href="${item.url}">
          <img
            src="${item.image}"
            alt="${item.title}"
            class="cart-item__image"
            loading="lazy"
            width="80"
            height="100"
          >
        </a>
        <div class="cart-item__details">
          <div class="cart-item__title">${item.product_title}</div>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-item__variant">${item.variant_title}</div>` : ''}
          <div class="cart-item__price">${formattedPrice}</div>

          <!-- Quantity Controls -->
          <div class="cart-item__quantity">
            <button
              class="cart-item__qty-btn"
              data-qty-decrease="${item.key}"
              aria-label="Decrease quantity"
            >−</button>
            <input
              type="number"
              class="cart-item__qty-input"
              value="${item.quantity}"
              min="1"
              data-qty-input="${item.key}"
              readonly
            >
            <button
              class="cart-item__qty-btn"
              data-qty-increase="${item.key}"
              aria-label="Increase quantity"
            >+</button>
          </div>

          <button class="cart-item__remove" data-remove-item="${item.key}" aria-label="Remove ${item.title}">
            Remove
          </button>
        </div>
      </div>
    `;
  }

  // Initialize cart item event listeners
  initCartItemListeners() {
    // Quantity increase
    document.querySelectorAll('[data-qty-increase]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const key = e.target.dataset.qtyIncrease;
        const input = document.querySelector(`[data-qty-input="${key}"]`);
        const newQty = parseInt(input.value) + 1;
        await this.updateCartItemQuantity(key, newQty);
      });
    });

    // Quantity decrease
    document.querySelectorAll('[data-qty-decrease]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const key = e.target.dataset.qtyDecrease;
        const input = document.querySelector(`[data-qty-input="${key}"]`);
        const newQty = Math.max(1, parseInt(input.value) - 1);
        await this.updateCartItemQuantity(key, newQty);
      });
    });

    // Remove item
    document.querySelectorAll('[data-remove-item]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const key = e.target.dataset.removeItem;
        await this.updateCartItemQuantity(key, 0);
      });
    });
  }

  // Update cart item quantity via AJAX
  async updateCartItemQuantity(key, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      });

      if (response.ok) {
        await this.updateCartDrawer();
        this.showToast(quantity === 0 ? 'Item removed from cart' : 'Cart updated');
      }
    } catch (error) {
      console.error('Error updating cart:', error);
      this.showToast('Error updating cart', 'error');
    }
  }

  // Add item to cart
  async addToCart(variantId, quantity = 1) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      });

      if (response.ok) {
        // Small delay to let Shopify session settle
        await new Promise(resolve => setTimeout(resolve, 150));
        await this.updateCartDrawer();

        // Open cart drawer via custom event (handled by cart-drawer.liquid and theme.liquid)
        document.dispatchEvent(new CustomEvent('cart:open'));

        this.showToast('Added to cart!', 'success');
        return true;
      } else {
        const error = await response.json();
        this.showToast(error.description || 'Error adding to cart', 'error');
        return false;
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showToast('Error adding to cart', 'error');
      return false;
    }
  }

  // ========================================
  // PREDICTIVE SEARCH
  // ========================================
  initPredictiveSearch() {
    const searchInput = document.querySelector('[data-predictive-search-input]');
    const searchResults = document.querySelector('[data-predictive-search-results]');

    if (!searchInput || !searchResults) return;

    let searchTimeout;

    searchInput.addEventListener('input', (e) => {
      const query = e.target.value.trim();

      clearTimeout(searchTimeout);

      if (query.length < 2) {
        searchResults.innerHTML = '';
        searchResults.classList.remove('visible');
        return;
      }

      searchTimeout = setTimeout(async () => {
        await this.performPredictiveSearch(query);
      }, 300);
    });

    // Close search results when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-predictive-search]')) {
        searchResults.classList.remove('visible');
      }
    });
  }

  async performPredictiveSearch(query) {
    const searchResults = document.querySelector('[data-predictive-search-results]');

    try {
      const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`);
      const data = await response.json();

      const products = data.resources.results.products || [];

      if (products.length === 0) {
        searchResults.innerHTML = '<div class="search-result__empty">No products found</div>';
      } else {
        searchResults.innerHTML = products.map(product => `
          <a href="${product.url}" class="search-result__item">
            <img src="${product.featured_image}" alt="${product.title}" class="search-result__image" loading="lazy">
            <div class="search-result__details">
              <div class="search-result__title">${product.title}</div>
              <div class="search-result__price">${this.formatMoney(product.price)}</div>
            </div>
          </a>
        `).join('');
      }

      searchResults.classList.add('visible');
    } catch (error) {
      console.error('Search error:', error);
    }
  }

  // ========================================
  // SCROLL ANIMATIONS
  // ========================================
  initScrollAnimations() {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Observe all elements with data-animate attribute
    document.querySelectorAll('[data-animate]').forEach(el => {
      observer.observe(el);
    });
  }

  // ========================================
  // PREMIUM TOAST NOTIFICATIONS
  // ========================================
  initToastNotifications() {
    if (!document.querySelector('.premium-toast-container')) {
      const container = document.createElement('div');
      container.className = 'premium-toast-container';
      document.body.appendChild(container);
    }
  }

  showToast(message, type = 'success') {
    const container = document.querySelector('.premium-toast-container');
    if (!container) return;

    const toast = document.createElement('div');
    toast.className = `premium-toast premium-toast--${type}`;

    const icon = type === 'success' ?
      `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"></polyline></svg>` :
      `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>`;

    toast.innerHTML = `
      <div class="premium-toast__content">
        <span class="premium-toast__icon">${icon}</span>
        <span class="premium-toast__message">${message}</span>
      </div>
    `;

    container.appendChild(toast);

    // Fade in
    setTimeout(() => toast.classList.add('is-visible'), 10);

    // Auto remove
    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 400);
    }, 3000);
  }

  formatMoney(cents, format) {
    if (typeof cents === 'string') { cents = cents.replace('.', ''); }
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || window.moneyFormat || '$ {{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision || 2;
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number == null) { return 0; }

      number = (number / 100).toFixed(precision);

      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? (decimal + parts[1]) : '';

      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }
}

// Global Toast Styles
const toastStyles = document.createElement('style');
toastStyles.textContent = `
  .premium-toast-container {
    position: fixed;
    bottom: 40px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10000;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    pointer-events: none;
  }
  .premium-toast {
    background: rgba(0, 0, 0, 0.95);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(201, 169, 110, 0.3);
    color: #fff;
    padding: 12px 24px;
    border-radius: 40px;
    box-shadow: 0 10px 30px rgba(0,0,0,0.4);
    transform: translateY(20px);
    opacity: 0;
    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    white-space: nowrap;
  }
  .premium-toast.is-visible {
    transform: translateY(0);
    opacity: 1;
  }
  .premium-toast__content {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .premium-toast__icon {
    display: flex;
    color: var(--color-gold, #C9A96E);
  }
  .premium-toast__message {
    font-family: 'Outfit', sans-serif;
    font-size: 0.9rem;
    font-weight: 600;
    letter-spacing: 0.02em;
  }
  .premium-toast--error .premium-toast__icon { color: #ff3b3b; }
`;
document.head.appendChild(toastStyles);

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.soloSupply = new SoloSupply();
  });
} else {
  window.soloSupply = new SoloSupply();
}

// Global quickAddToCart used by product-card.liquid onclick handlers
function quickAddToCart(variantId) {
  if (!variantId) return;
  if (window.soloSupply) {
    window.soloSupply.addToCart(variantId, 1);
  } else {
    // Fallback if SoloSupply hasn't initialized yet
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(r => r.json())
      .then(() => {
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.dispatchEvent(new CustomEvent('cart:open'));
      })
      .catch(console.error);
  }
}

// ========================================
// PREMIUM EFFECTS
// ========================================
(function () {
  'use strict';

  // ── Scroll Progress Bar ──
  const progressBar = document.getElementById('scroll-progress');
  window.addEventListener('scroll', () => {
    const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    if (progressBar && scrollHeight > 0) progressBar.style.width = (scrollTop / scrollHeight) * 100 + '%';
  }, { passive: true });

  // ── Glassmorphism Header on Scroll ──
  const header = document.querySelector('.header-solo');
  window.addEventListener('scroll', () => {
    header?.classList.toggle('scrolled', window.scrollY > 60);
  }, { passive: true });

  // ── Stagger Reveal (IntersectionObserver) ──

  const staggerObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        staggerObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -30px 0px' });

  document.querySelectorAll('.stagger-reveal, .text-reveal, .reveal, .reveal-left').forEach(el => {
    staggerObserver.observe(el);
  });

  // Global Safety Redirect for dead "Story" links
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a');
    if (link && link.getAttribute('href') && link.getAttribute('href').includes('/pages/our-story')) {
      e.preventDefault();
      window.location.href = '/#story-section';
    }
  });

  // ── Button Ripple Effect ──
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.btn--primary');
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const ripple = document.createElement('span');
    ripple.className = 'btn-ripple';
    const size = Math.max(rect.width, rect.height);
    ripple.style.width = ripple.style.height = size + 'px';
    ripple.style.left = (e.clientX - rect.left - size / 2) + 'px';
    ripple.style.top = (e.clientY - rect.top - size / 2) + 'px';
    btn.appendChild(ripple);
    ripple.addEventListener('animationend', () => ripple.remove());
  });

  // ── Hero Parallax ──
  const hero = document.querySelector('.hero-premium');
  if (hero) {
    window.addEventListener('scroll', () => {
      const scroll = window.scrollY;
      if (scroll < window.innerHeight) {
        const content = hero.querySelector('.hero-premium__content');
        if (content) content.style.transform = `translateY(${scroll * 0.15}px)`;
        const particles = hero.querySelector('.hero-particles');
        if (particles) particles.style.transform = `translateY(${scroll * 0.08}px)`;
      }
    }, { passive: true });
  }
})();

// ========================================
// ANIMATED STARFIELD CANVAS
// ========================================
(function () {
  const canvas = document.getElementById('starfield');
  if (!canvas) return;
  const ctx = canvas.getContext('2d');

  let W, H;
  const STAR_COUNT = 350;
  const stars = [];

  function resize() {
    W = canvas.width = window.innerWidth;
    H = canvas.height = document.documentElement.scrollHeight || window.innerHeight * 5;
  }

  class Star {
    constructor() { this.reset(); }
    reset() {
      this.x = Math.random() * (W || 1920);
      this.y = Math.random() * (H || 5000);
      this.radius = Math.random() * 1.8 + 0.3;
      this.baseAlpha = Math.random() * 0.7 + 0.3;
      this.alpha = this.baseAlpha;
      this.twinkleSpeed = Math.random() * 0.02 + 0.005;
      this.twinkleOffset = Math.random() * Math.PI * 2;
      this.driftX = (Math.random() - 0.5) * 0.08;
      this.driftY = -Math.random() * 0.15 - 0.02;
    }
    update(time) {
      this.alpha = this.baseAlpha + Math.sin(time * this.twinkleSpeed + this.twinkleOffset) * 0.3;
      this.alpha = Math.max(0.05, Math.min(1, this.alpha));
      this.x += this.driftX;
      this.y += this.driftY;
      if (this.y < -5) { this.y = H + 5; this.x = Math.random() * W; }
      if (this.x < -5) this.x = W + 5;
      if (this.x > W + 5) this.x = -5;
    }
    draw(ctx, scrollY) {
      const screenY = this.y - scrollY;
      if (screenY < -10 || screenY > window.innerHeight + 10) return;
      ctx.beginPath();
      ctx.arc(this.x, screenY, this.radius, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,255,255,${this.alpha})`;
      ctx.fill();
      if (this.radius > 1.2) {
        ctx.beginPath();
        ctx.arc(this.x, screenY, this.radius * 2.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${this.alpha * 0.08})`;
        ctx.fill();
      }
    }
  }

  function init() {
    resize();
    stars.length = 0;
    for (let i = 0; i < STAR_COUNT; i++) stars.push(new Star());
  }

  function animate(timestamp) {
    const scrollY = window.scrollY || 0;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (let i = 0; i < stars.length; i++) {
      stars[i].update(timestamp);
      stars[i].draw(ctx, scrollY);
    }
    requestAnimationFrame(animate);
  }

  window.addEventListener('resize', () => {
    W = window.innerWidth;
    H = document.documentElement.scrollHeight || window.innerHeight * 5;
  });

  init();
  requestAnimationFrame(animate);
})();

// ── End of SoloSupply Main JS ──
